/**
 * Comments Tab — inline comment surface for draft artifacts.
 *
 * Grouped by section per outline order, with whole-document comments pinned
 * at the top. A single document drafted section by section has no outline
 * sections; its inline comments anchor to the draft segments the editor shows,
 * so those groups follow the plan order and take the segment heading.
 * Supports filter by open/resolved/all and a "+ Whole document" entry point.
 * Each comment renders through CommentCard.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { MessageSquare, Plus, ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useCommentsStore } from '@/stores/comments-store'
import { useDraftingStore } from '@/stores/drafting-store'
import { useAgentStore } from '@/stores/agent-store'
import { scrollHighlightIntoView } from '@/lib/scroll-highlight'
import { resolveSectionTitle } from '@/lib/sectionTitle'
import { DOCUMENT_SECTION_KEY } from '@/types/scope'
import type { Comment } from '@/lib/comments-api'
import type { DraftSegment } from '@/lib/api'
import { CommentCard, type CommentJumpContext } from '@/components/drafting/CommentCard'
import { CommentComposer } from '@/components/drafting/CommentComposer'
import type { BidMemberInfo } from '@/components/ui/owner-assignment'

interface CommentsTabProps {
  bidId: string
  artifactId: string
  scopeKind?: 'unit' | 'artifact' | undefined
  bidMembers?: BidMemberInfo[] | undefined
  /** Route context for cross-section Jump; omitted ⇒ jumps stay in-document. */
  jumpContext?: CommentJumpContext | undefined
}

type FilterMode = 'open' | 'resolved' | 'all'

const EMPTY_COMMENTS: readonly never[] = Object.freeze([])
const EMPTY_SEGMENTS: readonly DraftSegment[] = Object.freeze([])
const EMPTY_ORDER: readonly string[] = Object.freeze([])

export function CommentsTab({
  bidId,
  artifactId,
  scopeKind = 'unit',
  bidMembers = [],
  jumpContext,
}: CommentsTabProps) {
  const commentsMap = useCommentsStore((s) => s.byTargetId)
  const comments = (commentsMap[artifactId] ?? EMPTY_COMMENTS) as Comment[]
  const loadingMap = useCommentsStore((s) => s.loading)
  const loading = loadingMap[artifactId] ?? false
  const errorMap = useCommentsStore((s) => s.error)
  const error = errorMap[artifactId] ?? null
  const load = useCommentsStore((s) => s.load)
  const loadPermissions = useCommentsStore((s) => s.loadPermissions)
  const permissionsMap = useCommentsStore((s) => s.permissions)
  const focusedCommentId = useCommentsStore((s) => s.focusedCommentId)
  const outline = useDraftingStore((s) => s.outline)
  // The single-document editor's segments — used only while the agent store
  // holds THIS artifact's document session. A multi-section response never has
  // one (its sessions are per section), and the Full response page can inherit
  // another editor's session, so matching on the session is what keeps both
  // untouched.
  const isDocumentSession = useAgentStore(
    (s) =>
      s.sessionInitContext?.scopeKind === 'artifact' &&
      s.sessionInitContext.artifactId === artifactId,
  )
  const storeSegments = useAgentStore((s) => s.segments)
  const storePlanSectionOrder = useAgentStore((s) => s.planSectionOrder)
  const segments = isDocumentSession ? storeSegments : EMPTY_SEGMENTS
  const planSectionOrder = isDocumentSession ? storePlanSectionOrder : EMPTY_ORDER

  const [filter, setFilter] = useState<FilterMode>('all')
  const [showResolved, setShowResolved] = useState(false)
  const [wholeDocComposerOpen, setWholeDocComposerOpen] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!bidId || !artifactId) return
    void load(bidId, artifactId, 'all')
    void loadPermissions(bidId, artifactId, null)
  }, [bidId, artifactId, load, loadPermissions])

  // A deep-linked comment must be reachable regardless of the filter the user
  // left the tab on. A resolved comment is hidden twice over — by the Open
  // filter and by the collapsed resolved group under "All".
  useEffect(() => {
    if (!focusedCommentId) return
    const target = comments.find((c) => c.id === focusedCommentId)
    if (!target) return
    setFilter((prev) => (prev === 'all' || prev === target.status ? prev : 'all'))
    if (target.status === 'resolved') setShowResolved(true)
  }, [focusedCommentId, comments])

  useEffect(() => {
    if (!focusedCommentId) return
    const card = listRef.current?.querySelector<HTMLElement>(
      `[data-comment-id="${focusedCommentId}"]`,
    )
    if (!card) return
    scrollHighlightIntoView(listRef.current, card, 0.5)
  }, [focusedCommentId, comments, filter, showResolved])

  const artifactPerms = permissionsMap[`${artifactId}:__artifact__`]

  const openCount = comments.filter((c) => c.status === 'open').length
  const resolvedCount = comments.filter((c) => c.status === 'resolved').length

  const filteredComments = useMemo(() => {
    if (filter === 'all') return comments
    return comments.filter((c) => c.status === filter)
  }, [comments, filter])

  // Group by section. Whole-doc comments pinned to '__whole_document__'.
  const groups = useMemo(() => {
    const wholeDoc: Comment[] = []
    const bySection = new Map<string, Comment[]>()

    for (const c of filteredComments) {
      if (filter === 'all' && c.status === 'resolved' && !showResolved) continue
      if (c.sectionId == null) {
        wholeDoc.push(c)
      } else {
        const existing = bySection.get(c.sectionId) ?? []
        existing.push(c)
        bySection.set(c.sectionId, existing)
      }
    }

    const orderedSectionIds =
      outline?.sections.map((s) => s.sectionKey).filter((k) => bySection.has(k)) ?? []

    // A single document's segment anchors follow the order its editor shows.
    for (const key of planSectionOrder) {
      if (bySection.has(key) && !orderedSectionIds.includes(key)) orderedSectionIds.push(key)
    }

    // Include any section_ids not in outline (e.g. __document__ sentinel or stale).
    for (const key of bySection.keys()) {
      if (!orderedSectionIds.includes(key)) orderedSectionIds.push(key)
    }

    return {
      wholeDoc,
      sections: orderedSectionIds.map((key) => ({
        sectionKey: key,
        comments: bySection.get(key) ?? [],
      })),
    }
  }, [filteredComments, outline, filter, showResolved, planSectionOrder])

  const sectionTitleFor = (key: string): string => {
    if (key === DOCUMENT_SECTION_KEY) return 'Document'
    const section = outline?.sections.find((s) => s.sectionKey === key)
    const outlineTitle = resolveSectionTitle(section)
    if (outlineTitle) return outlineTitle
    const segmentHeading = segments.find((segment) => segment.sectionId === key)?.heading.trim()
    return segmentHeading || key.slice(0, 8)
  }

  const canCreateWholeDoc = artifactPerms?.canCreate ?? false

  // A cross-section jump is only offered for a section that actually has an
  // editor route. Comments promoted from an AI review carry the *segment* key
  // the finding pointed at (e.g. 'overview'), which is not an outline section
  // key — building a route from it would land on a 404 editor, so those degrade
  // to no Jump at all. The same rule silences Jump until the outline has loaded.
  const outlineKeys = useMemo(
    () => new Set(outline?.sections.map((s) => s.sectionKey) ?? []),
    [outline],
  )
  const cardJumpContext = useMemo<CommentJumpContext | undefined>(() => {
    if (!jumpContext) return undefined
    return {
      ...jumpContext,
      buildSectionPath: (key) =>
        outlineKeys.has(key) ? jumpContext.buildSectionPath(key) : null,
    }
  }, [jumpContext, outlineKeys])

  if (loading && comments.length === 0) {
    return (
      <div className="flex items-center justify-center h-full py-8 px-4 text-sm text-muted-foreground">
        Loading comments…
      </div>
    )
  }

  const isEmpty = comments.length === 0

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 border-b border-border bg-transparent px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-meta">
            {openCount} open · {resolvedCount} resolved
          </span>
          <div className="flex items-center gap-0.5 bg-muted rounded-lg p-0.5">
            <FilterPill active={filter === 'open'} onClick={() => setFilter('open')}>
              Open
            </FilterPill>
            <FilterPill active={filter === 'resolved'} onClick={() => setFilter('resolved')}>
              Resolved
            </FilterPill>
            <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>
              All
            </FilterPill>
          </div>
        </div>
        {scopeKind !== 'artifact' && canCreateWholeDoc && (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs gap-1.5"
            onClick={() => setWholeDocComposerOpen(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Whole document
          </Button>
        )}
      </div>

      {wholeDocComposerOpen && (
        <div className="shrink-0 border-b border-border bg-transparent px-4 py-3">
          <CommentComposer
            bidId={bidId}
            targetId={artifactId}
            anchor={{ sectionId: null, startPos: null, endPos: null, quotedText: null }}
            bidMembers={bidMembers}
            onClose={() => setWholeDocComposerOpen(false)}
          />
        </div>
      )}

      {/*
        Error is checked BEFORE empty: a failed load also leaves the list
        empty, and "No comments yet" would then claim, falsely, that the
        section is clean. Never report absence we couldn't verify.
      */}
      <div className="flex-1 min-h-0 overflow-auto" ref={listRef}>
        {error ? (
          <div className="flex items-center justify-center py-8 px-4 text-sm text-destructive">
            {error}
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full py-12 px-4 text-center">
            <div className="h-12 w-12 rounded-full flex items-center justify-center mb-3 bg-muted">
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
            </div>
            <h4 className="text-sm font-medium mb-1">No comments yet</h4>
            <p className="text-meta max-w-[260px]">
              Select text in the draft to add one.
            </p>
          </div>
        ) : (
          <div className="py-2">
            {groups.wholeDoc.length > 0 && (
              <SectionGroup
                title="Whole document"
                comments={groups.wholeDoc}
                bidId={bidId}
                artifactId={artifactId}
                bidMembers={bidMembers}
                jumpContext={cardJumpContext}
              />
            )}
            {groups.sections.map((group) => (
              <SectionGroup
                key={group.sectionKey}
                title={sectionTitleFor(group.sectionKey)}
                comments={group.comments}
                bidId={bidId}
                artifactId={artifactId}
                bidMembers={bidMembers}
                jumpContext={cardJumpContext}
              />
            ))}
          </div>
        )}

        {/* Show resolved toggle (only when filter=all and we hid some resolved) */}
        {filter === 'all' && resolvedCount > 0 && !showResolved && (
          <button
            type="button"
            onClick={() => setShowResolved(true)}
            className="w-full text-left px-4 py-2 text-meta hover:bg-muted/50 flex items-center gap-1.5"
          >
            <ChevronRight className="h-3.5 w-3.5" />
            Show {resolvedCount} resolved
          </button>
        )}
        {filter === 'all' && resolvedCount > 0 && showResolved && (
          <button
            type="button"
            onClick={() => setShowResolved(false)}
            className="w-full text-left px-4 py-2 text-meta hover:bg-muted/50 flex items-center gap-1.5"
          >
            <ChevronDown className="h-3.5 w-3.5" />
            Hide resolved
          </button>
        )}
      </div>
    </div>
  )
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-xs px-2.5 py-0.5 rounded-md transition-colors',
        active
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

function SectionGroup({
  title,
  comments,
  bidId,
  artifactId,
  bidMembers,
  jumpContext,
}: {
  title: string
  comments: Comment[]
  bidId: string
  artifactId: string
  bidMembers: BidMemberInfo[]
  jumpContext: CommentJumpContext | undefined
}) {
  const openCount = comments.filter((c) => c.status === 'open').length
  return (
    <div className="mb-5">
      <div className="px-4 py-1.5 flex items-center justify-between text-xs">
        <span className="font-medium text-foreground uppercase tracking-wide">{title}</span>
        {openCount > 0 && (
          <span className="text-muted-foreground">
            {openCount} open
          </span>
        )}
      </div>
      <div className="flex flex-col gap-4 px-4">
        {comments.map((c) => (
          <CommentCard
            key={c.id}
            comment={c}
            bidId={bidId}
            targetId={artifactId}
            bidMembers={bidMembers}
            jumpContext={jumpContext}
          />
        ))}
      </div>
    </div>
  )
}
